export function roundDec(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Formats a volume and a weight as a totals label.
 *
 * @param {number} m3 - Volume in cubic meters.
 * @param {number} kgrs - Weight in kilograms.
 * @returns {string} - Label like "1.5 m³ / 20 kg".
 */
export function formatTotal(m3: number, kgrs: number): string {
  return `${roundDec(m3, 3)} m³ / ${roundDec(kgrs, 3)} kg`;
}

/**
 * Returns the pending items that the API reported as failed.
 *
 * The API answers `errors` with the sent items that failed, in the same order
 * they were sent and without their `id`, so they are matched in order by content.
 *
 * @template T - The type of the pending items.
 * @param {T[]} pends - The pending items stored locally.
 * @param {any[]} payloads - What was sent to the API for each pending item (same order as `pends`).
 * @param {any[]} errors - The `errors` list returned by the API.
 * @returns {T[]} - The pending items that must be kept to retry.
 */
export function getFailedPends<T>(
  pends: T[],
  payloads: any[],
  errors: any[],
): T[] {
  const serialize = (item: any) => {
    const { id, ...rest } = item;
    return JSON.stringify(rest);
  };
  const failed: T[] = [];
  let error_index = 0;
  pends.forEach((pend, index) => {
    if (
      error_index < errors.length &&
      serialize(payloads[index]) === serialize(errors[error_index])
    ) {
      failed.push(pend);
      error_index++;
    }
  });
  return failed;
}

/**
 * Builds the message to show when an HTTP request fails.
 *
 * @param {{ error: { detailed?: string } }} error - The error returned by HttpClient.
 * @param {string} default_message - Message used when the API does not send `detailed`.
 * @param {boolean} no_internet - Appends the connection warning when there is no connection.
 * @returns {string} - The message to show to the user.
 */
export function getErrorMessage(
  error: { error: { detailed?: string } },
  default_message: string,
  no_internet: boolean,
): string {
  let message = error.error?.hasOwnProperty('detailed')
    ? error.error.detailed!
    : default_message;
  no_internet && (message += ' No cuenta con una conexión estable a internet.');
  return message;
}

/**
 * Returns the plural form of a Spanish word based on the given quantity.
 *
 * The function uses standard Spanish pluralization rules:
 * - Ends in strong vowel (a, e, o) → add "s"
 * - Ends in consonant → add "es"
 * - Ends in "z" → replace with "ces"
 * - Ends in "ión" → drop accent and add "iones"
 * - If the word doesn't end in a letter → return original word
 *
 * Additionally, custom pluralization rules can be passed via the `exceptions` parameter.
 *
 * @param {string} word - The singular word to pluralize.
 * @param {number} quantity - The amount; pluralization is applied if quantity ≠ 1.
 * @param {[string, string][]} [exceptions] - Optional list of exception rules.
 *        Each item is a tuple: [originalWord, customSuffixToAppend]
 *        Example: [['kg', 's']] → 'kg' becomes 'kgs'
 * @returns {string} - The pluralized word, or the original word if quantity is 1 or the word is not eligible.
 */
export function pluralize(word: string, quantity: number, exceptions?: [string, string][]): string {
  if (quantity === 1 || !/[a-záéíóúñ]$/i.test(word)) {
    return word;
  }

  if (exceptions) {
    const exception = exceptions.find(exc => exc[0] === word);
    if (exception) {
      return word + exception[1];
    }
  }

  const strongVowels = ['a', 'e', 'o'];
  const lastLetter = word.slice(-1);

  // Ends in 'z' → replace with 'ces'
  if (lastLetter === 'z') {
    return word.slice(0, -1) + 'ces';
  }

  // Ends in 'ión' → drop accent and add 'iones'
  if (/ión$/.test(word)) {
    return word.slice(0, -3) + 'iones';
  }

  // Ends in strong vowel → add 's'
  if (strongVowels.includes(lastLetter)) {
    return word + 's';
  }

  // Ends in consonant → add 'es'
  if (/[bcdfghjklmnñpqrstvwxyz]$/i.test(lastLetter)) {
    return word + 'es';
  }

  // Fallback: just add 's'
  return word + 's';
}


/**
 * Replaces an item in an array based on a matching field value.
 *
 * @template T - The type of elements in the array.
 * @param {T[]} arr - The array to search and update.
 * @param {keyof T} search_field - The key in each object to compare against `search_value`.
 * @param {*} search_value - The value to match against the `search_field` property.
 * @param {T} newValue - The new item to replace the matched one.
 * @returns {T[] | null} The modified array with the replaced element (if found) or null.
 *
 * @example
 * const users = [{ id: 1 }, { id: 2 }];
 * const updated = replaceInArray(users, 'id', 2, { id: 2, name: 'John' });
 * // updated = [{ id: 1 }, { id: 2, name: 'John' }]
 */
export function replaceInArray<T>(
  arr: T[],
  search_field: keyof T,
  search_value: any,
  newValue: T
): T[] | null {
  const index = arr.findIndex((r: T) => r[search_field] === search_value);

  if (index !== -1) {
    arr[index] = newValue;
  }

  return arr;
}
